import { useState, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, Search, HelpCircle, Eye, Sparkles, X, Loader2 } from "lucide-react";
import { SopViewerDialog } from "@/components/sops/sop-viewer-dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

type SOP = {
  id: string;
  title: string;
  category: string | null;
  audience: "manus" | "humans" | "both";
  status: "draft" | "published";
  visibility: "all" | "selected";
  createdAt: string;
  updatedAt: string;
};

type FaqItem = { id: string; question: string; answer: string };

type FaqGroup = {
  sopId: string;
  sopTitle: string;
  sopCategory: string | null;
  faqs: FaqItem[];
};

type Category = { id: string; name: string; color: string };

export default function AssistantSopsPage() {
  const { toast } = useToast();

  // ── Procedures tab state ────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [viewingSopId, setViewingSopId] = useState<string | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  // ── FAQ tab state ────────────────────────────────────────────────────────
  const [faqSearchMode, setFaqSearchMode] = useState<"text" | "ai">("text");
  const [faqTextSearch, setFaqTextSearch] = useState("");
  const [faqAiQuery, setFaqAiQuery] = useState("");
  const [faqAiResultIds, setFaqAiResultIds] = useState<string[] | null>(null);
  const [faqCategoryFilter, setFaqCategoryFilter] = useState<string>("all");
  const faqTextInputRef = useRef<HTMLInputElement>(null);

  // ── Data queries ─────────────────────────────────────────────────────────

  const { data: sops = [], isLoading: sopsLoading } = useQuery<SOP[]>({
    queryKey: ["/api/sops", search, "published"],
    queryFn: async () => {
      const params = new URLSearchParams({ status: "published" });
      if (search) params.append("search", search);
      const res = await fetch(`/api/sops?${params}`);
      if (!res.ok) throw new Error("Failed to fetch SOPs");
      return res.json();
    },
  });

  const { data: faqGroups = [], isLoading: faqsLoading } = useQuery<FaqGroup[]>({
    queryKey: ["/api/sops/assistant-faqs"],
    queryFn: async () => {
      const res = await fetch("/api/sops/assistant-faqs");
      if (!res.ok) throw new Error("Failed to fetch FAQs");
      return res.json();
    },
  });

  // Categories scoped to SOPs — drives the FAQ category filter chips
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories", "sops"],
    queryFn: async () => {
      const res = await fetch("/api/categories?scope=sops");
      if (!res.ok) throw new Error("Failed to fetch categories");
      return res.json();
    },
  });

  // ── FAQ AI search ────────────────────────────────────────────────────────

  const faqAiSearch = useMutation({
    mutationFn: async (query: string) => {
      const res = await fetch("/api/sops/faq-ai-search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });
      if (!res.ok) throw new Error("AI search failed");
      return res.json() as Promise<{ ids: string[] }>;
    },
    onSuccess: (data) => setFaqAiResultIds(data.ids),
    onError: () => toast({ title: "AI search failed — please try again", variant: "destructive" }),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleOpen = (id: string) => { setViewingSopId(id); setIsViewerOpen(true); };

  const clearFaqSearch = useCallback(() => {
    setFaqTextSearch("");
    setFaqAiQuery("");
    setFaqAiResultIds(null);
    faqAiSearch.reset();
    setTimeout(() => faqTextInputRef.current?.focus(), 0);
  }, [faqAiSearch]);

  const switchFaqToAi = useCallback(() => {
    setFaqSearchMode("ai");
    setFaqTextSearch("");
    setFaqAiResultIds(null);
  }, []);

  const switchFaqToText = useCallback(() => {
    setFaqSearchMode("text");
    setFaqAiQuery("");
    setFaqAiResultIds(null);
    faqAiSearch.reset();
    setTimeout(() => faqTextInputRef.current?.focus(), 0);
  }, [faqAiSearch]);

  const runFaqAiSearch = useCallback(() => {
    if (!faqAiQuery.trim()) return;
    faqAiSearch.mutate(faqAiQuery.trim());
  }, [faqAiQuery, faqAiSearch]);

  // ── Derived: filtered FAQ groups ─────────────────────────────────────────

  const faqLower = faqTextSearch.toLowerCase();

  // Build a flat map of all FAQ items keyed by ID for AI ranking
  const allFaqsById: Record<string, { faqId: string; question: string; answer: string; sopId: string }> = {};
  for (const g of faqGroups) {
    for (const f of g.faqs) {
      allFaqsById[f.id] = { faqId: f.id, question: f.question, answer: f.answer, sopId: g.sopId };
    }
  }

  const aiRankMap: Record<string, number> = faqAiResultIds
    ? Object.fromEntries(faqAiResultIds.map((id, i) => [id, i + 1]))
    : {};

  const filteredFaqGroups: FaqGroup[] = (() => {
    let groups = faqGroups;

    // 1 — Category filter
    if (faqCategoryFilter !== "all") {
      groups = groups.filter((g) => g.sopCategory === faqCategoryFilter);
    }

    // 2a — AI mode: filter to matched IDs, rank within each group
    if (faqAiResultIds !== null) {
      const aiIdSet = new Set(faqAiResultIds);
      groups = groups
        .map((g) => ({
          ...g,
          faqs: g.faqs
            .filter((f) => aiIdSet.has(f.id))
            .sort((a, b) => (aiRankMap[a.id] ?? 999) - (aiRankMap[b.id] ?? 999)),
        }))
        .filter((g) => g.faqs.length > 0)
        // Sort groups so those with highest-ranked FAQs appear first
        .sort((a, b) => {
          const aTop = Math.min(...a.faqs.map((f) => aiRankMap[f.id] ?? 999));
          const bTop = Math.min(...b.faqs.map((f) => aiRankMap[f.id] ?? 999));
          return aTop - bTop;
        });
      return groups;
    }

    // 2b — Text mode: client-side filter
    if (faqLower) {
      groups = groups
        .map((g) => ({
          ...g,
          faqs: g.faqs.filter(
            (f) =>
              f.question.toLowerCase().includes(faqLower) ||
              f.answer.toLowerCase().includes(faqLower),
          ),
        }))
        .filter((g) => g.faqs.length > 0);
    }

    return groups;
  })();

  const totalFaqs = faqGroups.reduce((n, g) => n + g.faqs.length, 0);
  const filteredFaqCount = filteredFaqGroups.reduce((n, g) => n + g.faqs.length, 0);
  const aiActive = faqAiResultIds !== null;
  const isSearching = faqAiSearch.isPending;

  return (
    <div className="w-full min-h-screen p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-primary/10 rounded-lg hidden md:block">
          <BookOpen className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">SOPs &amp; FAQs</h1>
          <p className="text-muted-foreground text-sm">
            Standard operating procedures and quick-reference answers
          </p>
        </div>
      </div>

      <Tabs defaultValue="sops">
        <TabsList>
          <TabsTrigger value="sops" className="gap-2">
            <BookOpen className="w-4 h-4" />
            Procedures
            {sops.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">{sops.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="faqs" className="gap-2">
            <HelpCircle className="w-4 h-4" />
            All FAQs
            {totalFaqs > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">{totalFaqs}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Procedures Tab ── */}
        <TabsContent value="sops" className="mt-6 space-y-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search procedures…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-assistant-sops-search"
            />
          </div>

          {sopsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : sops.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground border rounded-lg border-dashed">
              <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No procedures available yet</p>
              <p className="text-sm mt-1">Published SOPs shared with you will appear here.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {sops.map((sop) => (
                <Card
                  key={sop.id}
                  className="hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => handleOpen(sop.id)}
                  data-testid={`card-sop-${sop.id}`}
                >
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <BookOpen className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{sop.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {sop.category && (
                          <span className="text-xs text-muted-foreground">{sop.category}</span>
                        )}
                        {sop.visibility === "selected" && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0"
                            data-testid={`badge-shared-with-you-${sop.id}`}
                          >
                            Shared with you
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 gap-1.5"
                      onClick={(e) => { e.stopPropagation(); handleOpen(sop.id); }}
                      data-testid={`button-view-assistant-sop-${sop.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── All FAQs Tab ── */}
        <TabsContent value="faqs" className="mt-6 space-y-4">

          {/* Search mode toggle */}
          <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
            <button
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                faqSearchMode === "text"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={switchFaqToText}
              type="button"
              data-testid="button-faq-search-mode-text"
            >
              <Search className="w-3.5 h-3.5" />
              Keyword
            </button>
            <button
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                faqSearchMode === "ai"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={switchFaqToAi}
              type="button"
              data-testid="button-faq-search-mode-ai"
            >
              <Sparkles className="w-3.5 h-3.5" />
              AI Search
            </button>
          </div>

          {/* Search input */}
          {faqSearchMode === "text" ? (
            <div className="relative max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={faqTextInputRef}
                className="pl-9 pr-9"
                placeholder="Search questions &amp; answers…"
                value={faqTextSearch}
                onChange={(e) => setFaqTextSearch(e.target.value)}
                data-testid="input-assistant-faq-search"
              />
              {faqTextSearch && (
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={clearFaqSearch}
                  type="button"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2 max-w-lg">
              <Textarea
                placeholder="Describe what you need — e.g. 'What do I do when a client misses a payment?'"
                value={faqAiQuery}
                onChange={(e) => setFaqAiQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) runFaqAiSearch(); }}
                rows={2}
                className="resize-none"
                data-testid="textarea-faq-ai-search"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Plain English — AI will find the most relevant FAQs.
                  {aiActive && (
                    <button className="ml-2 text-primary underline" onClick={clearFaqSearch} type="button">
                      Clear results
                    </button>
                  )}
                </p>
                <Button
                  size="sm"
                  onClick={runFaqAiSearch}
                  disabled={!faqAiQuery.trim() || isSearching}
                  data-testid="button-faq-ai-search"
                >
                  {isSearching ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching…</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" />Search</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Category filter chips */}
          {categories.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
              <button
                className={cn(
                  "flex-shrink-0 px-3 py-1 rounded-full text-sm font-medium border transition-colors",
                  faqCategoryFilter === "all"
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30",
                )}
                onClick={() => setFaqCategoryFilter("all")}
                type="button"
                data-testid="chip-faq-category-all"
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={cn(
                    "flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border transition-colors",
                    faqCategoryFilter === cat.name
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30",
                  )}
                  onClick={() => setFaqCategoryFilter(faqCategoryFilter === cat.name ? "all" : cat.name)}
                  type="button"
                  data-testid={`chip-faq-category-${cat.id}`}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: faqCategoryFilter === cat.name ? "currentColor" : cat.color,
                    }}
                  />
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {/* AI result summary */}
          {aiActive && (
            <div className="flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-primary shrink-0" />
              {filteredFaqCount === 0 ? (
                <span className="text-muted-foreground">No FAQs matched your query. Try rewording it.</span>
              ) : (
                <span>
                  <span className="font-medium text-primary">{filteredFaqCount}</span>
                  {" "}FAQ{filteredFaqCount !== 1 ? "s" : ""} found for &ldquo;
                  <span className="italic">{faqAiQuery}</span>&rdquo;
                </span>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            All published FAQs are available to every assistant, regardless of which procedures you have access to.
          </p>

          {/* FAQ list */}
          {faqsLoading || isSearching ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filteredFaqGroups.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground border rounded-lg border-dashed">
              <HelpCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">
                {faqTextSearch || aiActive ? "No matching FAQs" : "No FAQs yet"}
              </p>
              <p className="text-sm mt-1">
                {aiActive
                  ? "Try rephrasing your query, or switch to Keyword search."
                  : faqTextSearch
                  ? "Try a different search term."
                  : "FAQs added to published SOPs will appear here."}
              </p>
            </div>
          ) : (
            <Accordion type="multiple" className="space-y-3">
              {filteredFaqGroups.map((group) => (
                <div key={group.sopId} className="border rounded-lg overflow-hidden">
                  {/* Group header */}
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/40 border-b">
                    <BookOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium">{group.sopTitle}</span>
                    {group.sopCategory && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        ·
                        {categories.find((c) => c.name === group.sopCategory) && (
                          <span
                            className="w-1.5 h-1.5 rounded-full inline-block"
                            style={{
                              backgroundColor: categories.find((c) => c.name === group.sopCategory)?.color,
                            }}
                          />
                        )}
                        {group.sopCategory}
                      </span>
                    )}
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {group.faqs.length} {group.faqs.length === 1 ? "FAQ" : "FAQs"}
                    </Badge>
                  </div>
                  {/* FAQs */}
                  <Accordion type="multiple">
                    {group.faqs.map((faq) => (
                      <AccordionItem
                        key={faq.id}
                        value={faq.id}
                        className="border-0 border-b last:border-0"
                        data-testid={`faq-item-${faq.id}`}
                      >
                        <AccordionTrigger className="px-4 py-3 text-sm font-medium text-left hover:no-underline hover:bg-muted/20">
                          <span className="flex items-center gap-2 flex-1 min-w-0">
                            {aiActive && aiRankMap[faq.id] && (
                              <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center">
                                {aiRankMap[faq.id]}
                              </span>
                            )}
                            <span className="truncate text-left">{faq.question}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </Accordion>
          )}
        </TabsContent>
      </Tabs>

      <SopViewerDialog
        open={isViewerOpen}
        onOpenChange={setIsViewerOpen}
        sopId={viewingSopId}
      />
    </div>
  );
}
